import { aboutContent, siteInfo } from '@/mocks/site';
import SchemaOrg from '@/components/feature/SchemaOrg';
import Breadcrumb from '@/components/feature/Breadcrumb';

const FORM_SUBMIT_ADDR = 'https://mastinfo.github.io/api/form/d9u558tca23r0tr1aqu0';
const HONEYPOT_FIELD = 'website_alt';

export default function AboutPage() {
  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = (formData.get(HONEYPOT_FIELD) as string || '').trim();
    if (honeypot) {
      form.reset();
      return;
    }

    try {
      const response = await fetch(FORM_SUBMIT_ADDR, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const text = await response.text();
      let parsed: Record<string, unknown> = {};
      try { parsed = JSON.parse(text); } catch { /* ignore parse error */ }

      if (response.ok && parsed?.code === 'OK') {
        form.reset();
        alert('感谢您的留言！我们会尽快与您联系。');
      } else {
        const msg = parsed?.meta?.message || parsed?.message || '提交失败，请稍后再试。';
        alert(msg);
      }
    } catch {
      alert('网络错误，请稍后再试。');
    }
  };

  return (
    <>
      <SchemaOrg
        type="Organization"
        additionalData={{
          name: siteInfo.name,
          url: siteInfo.url,
          description: siteInfo.description,
          email: siteInfo.email,
          telephone: siteInfo.phone,
          address: {
            '@type': 'PostalAddress',
            streetAddress: siteInfo.address,
            addressLocality: '北京',
            addressCountry: 'CN',
          },
        }}
      />

      <div className="min-h-screen bg-background-50 pt-16">
        {/* Header */}
        <section className="w-full bg-gradient-to-br from-primary-50 via-background-50 to-accent-50/30 py-12 md:py-16">
          <div className="max-w-6xl mx-auto px-4 md:px-6">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: '关于我们' },
              ]}
            />
            <div className="mt-6">
              <h1 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-3" style={{ fontFamily: 'var(--font-heading)' }}>
                关于我们
              </h1>
              <p className="text-foreground-600 max-w-2xl text-sm md:text-base leading-relaxed">
                了解「自慰方式完全指南」的品牌故事、使命与团队
              </p>
            </div>
          </div>
        </section>

        {/* Brand Intro */}
        <section className="w-full px-4 md:px-6 py-14 md:py-20">
          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
              <div>
                <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-6" style={{ fontFamily: 'var(--font-heading)' }}>
                  {aboutContent.brandIntro.title}
                </h2>
                {aboutContent.brandIntro.sections.map((section, i) => (
                  <div key={i} className="mb-6">
                    <h3 className="text-lg font-semibold text-foreground-900 mb-2" style={{ fontFamily: 'var(--font-heading)' }}>
                      {section.heading}
                    </h3>
                    <p className="text-sm text-foreground-600 leading-relaxed">
                      {section.content}
                    </p>
                  </div>
                ))}
              </div>
              <div className="relative h-[400px] rounded-2xl overflow-hidden">
                <img
                  src="https://mastinfo.github.io/api/search-image?query=Warm%20minimalist%20healthcare%20team%20illustration%20with%20soft%20cream%20background%2C%20medical%20professionals%20collaborating%2C%20calm%20and%20trustworthy%20atmosphere%2C%20modern%20editorial%20aesthetic%2C%20diverse%20team%2C%20professional%20warm%20lighting&width=800&height=600&seq=about-team-01&orientation=portrait"
                  alt="我们的团队"
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Reputation */}
        <section className="w-full px-4 md:px-6 py-14 md:py-20 bg-background-100">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 text-center mb-12" style={{ fontFamily: 'var(--font-heading)' }}>
              {aboutContent.reputation.title}
            </h2>

            {/* Stats */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6 mb-14">
              {aboutContent.reputation.items.map((item, i) => (
                <div key={i} className="bg-white rounded-xl border border-background-200/70 p-6 text-center">
                  <div className="w-12 h-12 mx-auto mb-3 rounded-xl bg-accent-100 flex items-center justify-center">
                    <i className={`${item.icon} text-xl text-accent-600`}></i>
                  </div>
                  <div className="text-2xl md:text-3xl font-bold text-foreground-950 mb-1" style={{ fontFamily: 'var(--font-heading)' }}>
                    {item.stat}
                  </div>
                  <p className="text-xs text-foreground-500">{item.label}</p>
                </div>
              ))}
            </div>

            {/* Testimonials */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
              {aboutContent.reputation.testimonials.map((t, i) => (
                <div key={i} className="bg-white rounded-xl border border-background-200/70 p-6 md:p-7">
                  <div className="flex items-center gap-0.5 mb-4 text-amber-400">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <i key={star} className="ri-star-fill text-sm"></i>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed mb-4 italic">
                    「{t.quote}」
                  </p>
                  <p className="text-xs font-medium text-foreground-900">{t.author}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Contact */}
        <section className="w-full px-4 md:px-6 py-14 md:py-20">
          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16">
              <div>
                <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-4" style={{ fontFamily: 'var(--font-heading)' }}>
                  {aboutContent.contact.title}
                </h2>
                <p className="text-sm text-foreground-600 leading-relaxed mb-8">
                  {aboutContent.contact.description}
                </p>
                <div className="space-y-4">
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-xl bg-primary-100 flex items-center justify-center flex-shrink-0">
                      <i className="ri-mail-line text-primary-600"></i>
                    </div>
                    <div>
                      <p className="text-xs text-foreground-400 mb-0.5">电子邮箱</p>
                      <a href={`mailto:${aboutContent.contact.email}`} className="text-sm font-medium text-foreground-900 hover:text-primary-600 transition-colors">
                        {aboutContent.contact.email}
                      </a>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-xl bg-accent-100 flex items-center justify-center flex-shrink-0">
                      <i className="ri-phone-line text-accent-600"></i>
                    </div>
                    <div>
                      <p className="text-xs text-foreground-400 mb-0.5">联系电话</p>
                      <a href={`tel:${aboutContent.contact.phone}`} className="text-sm font-medium text-foreground-900 hover:text-primary-600 transition-colors">
                        {aboutContent.contact.phone}
                      </a>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-xl bg-secondary-100 flex items-center justify-center flex-shrink-0">
                      <i className="ri-map-pin-line text-secondary-600"></i>
                    </div>
                    <div>
                      <p className="text-xs text-foreground-400 mb-0.5">办公地址</p>
                      <p className="text-sm font-medium text-foreground-900">{aboutContent.contact.address}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center flex-shrink-0">
                      <i className="ri-time-line text-amber-600"></i>
                    </div>
                    <div>
                      <p className="text-xs text-foreground-400 mb-0.5">工作时间</p>
                      <p className="text-sm font-medium text-foreground-900">{aboutContent.contact.workingHours}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Contact Form */}
              <div className="bg-white rounded-2xl border border-background-200/70 p-6 md:p-8">
                <h3 className="text-lg font-semibold text-foreground-950 mb-6" style={{ fontFamily: 'var(--font-heading)' }}>
                  给我们留言
                </h3>
                <form id="contact-form" data-readdy-form="" onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-foreground-700 mb-1.5">姓名</label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      required
                      className="w-full px-4 py-2.5 text-sm border border-background-200 rounded-lg bg-background-50 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-all"
                      placeholder="请输入您的姓名"
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-foreground-700 mb-1.5">邮箱</label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      required
                      className="w-full px-4 py-2.5 text-sm border border-background-200 rounded-lg bg-background-50 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-all"
                      placeholder="请输入您的邮箱"
                    />
                  </div>
                  <div>
                    <label htmlFor="message" className="block text-sm font-medium text-foreground-700 mb-1.5">留言内容</label>
                    <textarea
                      id="message"
                      name="message"
                      required
                      maxLength={500}
                      rows={4}
                      className="w-full px-4 py-2.5 text-sm border border-background-200 rounded-lg bg-background-50 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-all resize-none"
                      placeholder="请输入您的留言（500字以内）"
                    ></textarea>
                    <p className="text-xs text-foreground-400 mt-1">最多500个字符</p>
                  </div>
                  {/* Honeypot */}
                  <input
                    type="text"
                    name={HONEYPOT_FIELD}
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                    className="honeypot-field"
                  />
                  <button
                    type="submit"
                    className="w-full px-6 py-3 bg-primary-500 hover:bg-primary-600 text-white rounded-lg text-sm font-medium transition-colors whitespace-nowrap"
                  >
                    发送留言
                  </button>
                </form>
              </div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
}